import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import React from "react";
import CloudIcon from "@mui/icons-material/Cloud";
import useGetCloudNavPermission from "@features/unified-side-nav-feature/hooks/useGetCloudNavPermission";

export function useGetCloudNavItems() {
  const { hasPermission, hasRoleIn} = useClaims();

  const isAdmin = hasRoleIn("CustomerAdmin", "ShiAdmin");

  const {
    showCloudNav,
    contosoDemo,
    hasAwsMasterPayerAccounts,
    hasAzureBackupIntegrations,
    hasAzureComplianceIntegrations,
    hasAzureGovernanceReportsIntegration,
    hasAzureRecommendations,
    awsBillingReport,
    hasGcpUsageReportIntegrations,
    hasDatadogUsageReports,
    hasCloudAccounts,
  } = useGetCloudNavPermission();

   const hasCloudReports =
    hasAzureRecommendations || awsBillingReport || hasGcpUsageReportIntegrations || !!hasAzureRecommendations


  const cloudChildren = [
        {
          id: "cloudAccounts",
          type: "Link",
          displayName: "Subscriptions",
          path: "/cloud/subscriptions",
          visible:
            hasPermission(ShiOnePermission.awsAccountRead) ||
            hasPermission(ShiOnePermission.azureAccountRead) ||
            hasPermission(ShiOnePermission.o365AccountRead) ||
            hasPermission(ShiOnePermission.gcpAccountRead),
        },
        {
          id: "cloudCostCenters",
          type: "Link",
          displayName: "Cost Centers",
          path: "/cloud/cost-centers",
          visible: hasCloudAccounts && isAdmin,
        },
        {
          id: "cloudConsumption",
          type: "Link",
          displayName: "Consumption",
          path: "/cloud/consumption",
          visible:  hasCloudReports &&
            (isAdmin ||
              hasPermission(ShiOnePermission.multicloudConsumptionRead))
        },
        {
          id: "monitoring",
          type: "Link",
          displayName: "Monitoring",
          path: "/cloud/monitoring",
          visible:       hasPermission(ShiOnePermission.azureMonitoringAlertRead) ||
            hasPermission(ShiOnePermission.awsMonitoringAlertRead)
        },
        {
          id: "cloudAws",
          type: "Group",
          displayName: "AWS",
          visible:
            hasPermission(ShiOnePermission.awsAccountRead) ||
            hasPermission(ShiOnePermission.awsConsumptionRead) ||
            hasPermission(ShiOnePermission.awsBlueprintDeploy) ||
            (isAdmin && hasAwsMasterPayerAccounts) ||
            hasPermission(ShiOnePermission.serviceInvoiceRead) ||
            hasPermission(ShiOnePermission.awsMonitoringAlertRead) ||
            hasPermission(ShiOnePermission.awsRecommendationRead) ||
            hasPermission(ShiOnePermission.awsComplianceRead),
          children: [
            {
              id: "cloudAwsAccounts",
              type: "Link",
              displayName: "Accounts",
              path: "/cloud/aws/accounts",
              visible: hasPermission(ShiOnePermission.awsAccountRead),
            },
            {
              id: "cloudAwsReports",
              type: "Link",
              displayName: "Consumption",
              path: "/cloud/aws/reports",
              visible: hasPermission(ShiOnePermission.awsConsumptionRead),
            },
            {
              id: "cloudAwsBlueprints",
              type: "Link",
              displayName: "Blueprints",
              path: "/cloud/aws/blueprints",
              visible: hasPermission(ShiOnePermission.awsBlueprintDeploy),
            },
            {
              id: "cloudAwsCostAllocationTags",
              type: "Link",
              displayName: "Cost Allocation Tags",
              path: "/cloud/aws/cat",
              visible: isAdmin && hasAwsMasterPayerAccounts,
            },
            {
              id: "cloudAwsInvoices",
              type: "Link",
              displayName: "Invoices",
              path: "/cloud/aws/invoices",
              visible: hasPermission(ShiOnePermission.serviceInvoiceRead),
            },
            {
              id: "cloudAwsMonitoring",
              type: "Link",
              displayName: "Monitoring",
              path: "/cloud/aws/monitoring",
              visible: hasPermission(ShiOnePermission.awsMonitoringAlertRead),
            },
            {
              id: "cloudAwsRecommendations",
              type: "Link",
              displayName: "Recommendations",
              path: "/cloud/aws/recommendations",
              visible: hasPermission(ShiOnePermission.awsRecommendationRead),
            },
            {
              id: "cloudAwsGovernanceReports",
              type: "Link",
              displayName: "Governance",
              path: "/cloud/aws/governance",
              visible: hasPermission(ShiOnePermission.awsComplianceRead),
            },
          ],
        },
        {
          id: "cloudAzure",
          type: "Group",
          displayName: "Azure",
          visible:
            (hasPermission(ShiOnePermission.azureBackupAlertRead) &&
              hasAzureBackupIntegrations) ||
            hasPermission(ShiOnePermission.azureBlueprintDeploy) ||
            hasPermission(ShiOnePermission.serviceInvoiceRead) ||
            hasPermission(ShiOnePermission.azureMonitoringAlertRead) ||
            hasPermission(ShiOnePermission.azureRecommendationRead) ||
            hasPermission(ShiOnePermission.azureConsumptionRead) ||
            (hasAzureComplianceIntegrations &&
              hasPermission(ShiOnePermission.azureComplianceRead)) ||
            (hasAzureGovernanceReportsIntegration &&
              hasPermission(ShiOnePermission.azureComplianceRead)) ||
            hasPermission(ShiOnePermission.azureAccountRead),
          children: [
            {
              id: "cloudAzureBackup",
              type: "Link",
              displayName: "Backup",
              path: "/cloud/azure/backup",
              visible:
                hasPermission(ShiOnePermission.azureBackupAlertRead) &&
                hasAzureBackupIntegrations,
            },
            {
              id: "cloudAzureBlueprints",
              type: "Link",
              displayName: "Blueprints",
              path: "/cloud/azure/blueprints",
              visible: hasPermission(ShiOnePermission.azureBlueprintDeploy),
            },
            {
              id: "cloudAzureInvoices",
              type: "Link",
              displayName: "Invoices",
              path: "/cloud/azure/invoices",
              visible: hasPermission(ShiOnePermission.serviceInvoiceRead),
            },
            {
              id: "cloudAzureMonitoring",
              type: "Link",
              displayName: "Monitoring",
              path: "/cloud/azure/monitoring",
              visible: hasPermission(ShiOnePermission.azureMonitoringAlertRead),
            },
            {
              id: "cloudAzureRecommendations",
              type: "Link",
              displayName: "Recommendations",
              path: "/cloud/azure/recommendations",
              visible: hasPermission(ShiOnePermission.azureRecommendationRead),
            },
            {
              id: "cloudAzureRecommendationsAllUpReport",
              type: "Link",
              displayName: "All-Up Recommendations",
              path: "/cloud/azure/reports/all-up/recommendations",
              visible: hasPermission(ShiOnePermission.azureRecommendationRead),
            },
            {
              id: "cloudAzureReports",
              type: "Link",
              displayName: "Reports",
              path: "/cloud/azure/reports",
              visible: hasPermission(ShiOnePermission.azureConsumptionRead),
            },
            {
              id: "cloudAzureSecurityCompliance",
              type: "Link",
              displayName: "Security & Compliance",
              path: "/cloud/azure/securitycompliance",
              visible:
                hasAzureComplianceIntegrations &&
                hasPermission(ShiOnePermission.azureComplianceRead),
            },
            {
              id: "cloudAzureGovernance",
              type: "Link",
              displayName: "Governance",
              path: "/cloud/azure/governance",
              visible:
                hasAzureGovernanceReportsIntegration &&
                hasPermission(ShiOnePermission.azureComplianceRead),
            },
            {
              id: "cloudAzureSubscriptions",
              type: "Link",
              displayName: "Subscriptions",
              path: "/cloud/azure/subscriptions",
              visible: hasPermission(ShiOnePermission.azureAccountRead),
            },
          ],
        },
        {
          id: "cloudGcp",
          type: "Group",
          displayName: "GCP",
          visible:
            hasPermission(ShiOnePermission.serviceInvoiceRead) ||
            hasPermission(ShiOnePermission.gcpAccountRead) ||
            hasPermission(ShiOnePermission.gcpConsumptionRead),
          children: [
            {
              id: "cloudGcpInvoices",
              type: "Link",
              displayName: "Invoices",
              path: "/cloud/gcp/invoices",
              visible: hasPermission(ShiOnePermission.serviceInvoiceRead),
            },
            {
              id: "cloudGcpProjects",
              type: "Link",
              displayName: "Projects",
              path: "/cloud/gcp/projects",
              visible: hasPermission(ShiOnePermission.gcpAccountRead),
            },
            {
              id: "cloudGcpReports",
              type: "Link",
              displayName: "Reports",
              path: "/cloud/gcp/reports",
              visible: hasPermission(ShiOnePermission.gcpConsumptionRead),
            },
          ],
        },
        {
          id: "cloudOffice365",
          type: "Group",
          displayName: "Office 365",
          visible:
            hasPermission(ShiOnePermission.serviceInvoiceRead) ||
            hasPermission(ShiOnePermission.o365AccountRead),
          children: [
            {
              id: "cloudOffice365Invoices",
              type: "Link",
              displayName: "Invoices",
              path: "/cloud/office365/invoices",
              visible: hasPermission(ShiOnePermission.serviceInvoiceRead),
            },
            {
              id: "cloudOffice365Subscriptions",
              type: "Link",
              displayName: "Subscriptions",
              path: "/cloud/office365/subscriptions",
              visible: hasPermission(ShiOnePermission.o365AccountRead),
            },
          ],
        },
        {
          id: "cloudDatadog",
          type: "Group",
          displayName: "Datadog",
          visible:
            (!isAdmin && hasPermission(ShiOnePermission.datadogConsumptionRead)) ||
            (isAdmin && hasDatadogUsageReports) ||
            (isAdmin && contosoDemo),
          children: [
            {
              id: "cloudDatadogUsageReports",
              type: "Link",
              displayName: "Reports",
              path: "/cloud/datadog/reports",
              visible:
                (!isAdmin &&
                  hasPermission(ShiOnePermission.datadogConsumptionRead) &&
                  hasDatadogUsageReports) ||
                (isAdmin && hasDatadogUsageReports) ||
                (isAdmin && contosoDemo),
            },
          ],
        },
  ];

  return [
    {
      id: "cloud",
      type: "Dashboard",
      displayName: "Cloud",
      icon: <CloudIcon />,
      path: "/cloud",
      visible:
        (!isAdmin && cloudChildren.some((c) => c.visible)) ||
        (isAdmin && showCloudNav),
      children: cloudChildren,
    },
  ];
}
